/**
 * /admin/system — die Betriebsseite aus spec §3 Punkt 10.
 *
 * Sie beantwortet eine einzige Frage: hängt irgendetwas? Welcher Commit läuft, wie viele Webhook-
 * Ereignisse warten oder sind gescheitert, wie viele Mails liegen in der Warteschlange oder sind
 * endgültig gescheitert, welche ausgestellte Rechnung hat noch kein PDF, was sagten die nächtlichen
 * Invarianten zuletzt, wann wurde jeder Auftraggeber gesichert und wie groß ist D1.
 *
 * Dazu die beiden Werkzeuge, die dieselbe Seite braucht: Webhooks eines Zeitraums erneut einspielen und
 * eine Testmail über die Produktionsstrecke schicken.
 *
 * Alle Zahlen kommen aus `GET /api/admin/system` (routes/admin.ts) und werden hier nur angezeigt —
 * summiert wird nichts, umgerechnet wird einzig die Datenbankgröße von Bytes in Megabyte.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState, type ReactElement } from 'react'
import { testEmailSchema } from '../../../shared/schemas/email'
import { replayQuerySchema } from '../../../shared/schemas/ingest'
import { api, type SystemStatus } from '../../api'
import { RequireSession } from '../../auth'
import { Feld } from '../../components/Feld'
import { Knopf } from '../../components/Knopf'
import { Leiste } from '../../components/Leiste'
import { Tabelle } from '../../components/Tabelle'
import { Zustand, fehlermeldung } from '../../components/Zustand'
import { datumZeit } from '../../format'

const MEGABYTE = 1024 * 1024
const ZAHL = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

/** Die erste zod-Meldung mit dem Feld, das sie meint — wie in den Bildschirmen des Auftraggebers. */
interface Eingabefehler {
  feld: string | null
  meldung: string
}

interface ZodMeldung { readonly path: readonly PropertyKey[]; readonly message: string }

function ersterFehler(fehler: { issues: readonly ZodMeldung[] }): Eingabefehler {
  const erste = fehler.issues[0]
  if (erste === undefined) return { feld: null, meldung: 'Die Eingabe ist ungültig.' }
  const pfad = erste.path.filter((teil): teil is string => typeof teil === 'string').join('.')
  return { feld: pfad === '' ? null : pfad, meldung: erste.message }
}

function meldungFuer(fehler: Eingabefehler | null, feld: string): string | undefined {
  return fehler !== null && fehler.feld === feld ? fehler.meldung : undefined
}

/**
 * Was die letzte nächtliche Prüfung ergeben hat — die Kennungen der gerissenen Invarianten wörtlich.
 *
 * `failed` kann leer sein, obwohl der Lauf nicht in Ordnung war: `invariantSummary` (routes/admin.ts)
 * antwortet mit `{ok: false, failed: []}`, wenn die Prüfzeile kein `after_json` hat, wenn das JSON
 * nicht lesbar ist oder wenn es keine `violations` nennt. Dann steht hier, dass es einen Befund gibt
 * und dass seine Kennungen fehlen — statt eines Doppelpunkts, hinter dem nichts kommt.
 */
function invariantenText(lauf: SystemStatus['per_tenant'][number]['last_invariant_run']): string {
  if (lauf === null) return 'nie gelaufen'
  if (lauf.ok) return `ohne Befund, ${datumZeit(lauf.at)}`
  if (lauf.failed.length === 0) return `Befund ohne lesbare Kennungen, ${datumZeit(lauf.at)}`
  return `Befund: ${lauf.failed.join(', ')}, ${datumZeit(lauf.at)}`
}

/**
 * Ohne Sitzung antwortet `GET /api/admin/system` mit 401, und der Bildschirm zeigte dann eine
 * Fehlerkarte mit dem Satz des Servers, statt zur Anmeldung zu führen. Der Zugangsschutz gehört
 * deshalb wie bei jedem anderen Bildschirm an die Route.
 */
function SystemRoute(): ReactElement {
  return (
    <RequireSession>
      <SystemScreen />
    </RequireSession>
  )
}

export const Route = createFileRoute('/admin/system')({ component: SystemRoute })

export function SystemScreen(): ReactElement {
  const zustand = useQuery({
    queryKey: ['admin', 'system'],
    queryFn: () => api.get<SystemStatus>('/api/admin/system'),
  })

  const daten = zustand.data

  return (
    <div className="seite">
      <h1>System</h1>

      {zustand.isPending && <Zustand art="laedt" beschriftung="Der Systemzustand wird geladen." />}
      {zustand.isError && (
        <Zustand art="fehler" fehler={zustand.error} onWiederholen={() => { void zustand.refetch() }} />
      )}

      {daten !== undefined && (
        <>
          <p>
            Laufender Stand: <span className="mono">{daten.git_sha}</span>{' · '}
            <a href="/api/health">Health-Endpunkt öffnen</a>
          </p>

          <dl className="kennzahlen">
            <Kennzahl titel="Auftraggeber" wert={String(daten.tenants)} />
            <Kennzahl titel="Webhook-Ereignisse offen" wert={String(daten.pending_events)} />
            <Kennzahl titel="Webhook-Ereignisse fehlerhaft" wert={String(daten.failed_events)} />
            <Kennzahl titel="E-Mails in der Warteschlange" wert={String(daten.queued_emails)} />
            <Kennzahl titel="E-Mails endgültig gescheitert" wert={String(daten.failed_emails)} />
            <Kennzahl titel="Rechnungen ohne Dokument" wert={String(daten.invoices_without_document)} />
            <Kennzahl
              titel="Datenbankgröße"
              wert={daten.d1_size_bytes === null ? 'unbekannt' : `${ZAHL.format(daten.d1_size_bytes / MEGABYTE)} MB`}
            />
          </dl>

          <Tabelle
            beschriftung="Zustand je Auftraggeber"
            kopf={
              <tr>
                <th scope="col">Auftraggeber</th>
                <th scope="col">Ereignisse offen / fehlerhaft</th>
                <th scope="col">Mails offen / gescheitert</th>
                <th scope="col">Rechnungen ohne PDF</th>
                <th scope="col">Letzte Invariantenprüfung</th>
                <th scope="col">Letzte Sicherung</th>
              </tr>
            }
          >
            {daten.per_tenant.map((zeile) => (
              <tr key={zeile.tenant_id}>
                <th scope="row" className="mono">{zeile.slug}</th>
                <td className="mono">{`${zeile.pending_events} / ${zeile.failed_events}`}</td>
                <td className="mono">{`${zeile.queued_emails} / ${zeile.failed_emails}`}</td>
                <td className="mono">{zeile.invoices_without_document}</td>
                <td>{invariantenText(zeile.last_invariant_run)}</td>
                <td>{zeile.last_backup === null ? 'keine' : datumZeit(zeile.last_backup.at)}</td>
              </tr>
            ))}
          </Tabelle>

          <Replay auftraggeber={daten.per_tenant} />
          <Testmail />
        </>
      )}
    </div>
  )
}

function Kennzahl({ titel, wert }: { titel: string; wert: string }): ReactElement {
  return (
    <div>
      <dt>{titel}</dt>
      <dd className="mono">{wert}</dd>
    </div>
  )
}

// -----------------------------------------------------------------------------------------------------
// Webhooks erneut einspielen
// -----------------------------------------------------------------------------------------------------

function Replay({ auftraggeber }: { auftraggeber: SystemStatus['per_tenant'] }): ReactElement {
  const [tenant, setzeTenant] = useState('')
  const [von, setzeVon] = useState('')
  const [bis, setzeBis] = useState('')
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)

  const replay = useMutation({
    mutationFn: (pfad: string) => api.post<{ replayed: number }>(pfad, {}),
  })

  return (
    <section aria-label="Webhooks erneut einspielen">
      <h2>Webhooks erneut einspielen</h2>
      <p className="hinweis">
        Spielt die gespeicherten Roh-Ereignisse eines Zeitraums noch einmal durch die Verarbeitung.
        Nichts wird doppelt gebucht — die Verarbeitung ist auf denselben Rohdaten wiederholbar.
      </p>
      <Leiste beschriftung="Zeitraum und Auftraggeber des Wiedereinspielens">
        <Feld label="Auftraggeber" htmlFor="rp-tenant" fehler={meldungFuer(pruefFehler, 'tenant')}>
          <select
            id="rp-tenant"
            value={tenant}
            onChange={(ereignis) => { setzeTenant(ereignis.target.value) }}
          >
            <option value="">bitte wählen</option>
            {auftraggeber.map((zeile) => (
              <option key={zeile.tenant_id} value={zeile.tenant_id}>{zeile.slug}</option>
            ))}
          </select>
        </Feld>
        <Feld label="Von" htmlFor="rp-von" fehler={meldungFuer(pruefFehler, 'from')}>
          <input id="rp-von" type="date" value={von} onChange={(e) => { setzeVon(e.target.value) }} />
        </Feld>
        <Feld label="Bis" htmlFor="rp-bis" fehler={meldungFuer(pruefFehler, 'to')}>
          <input id="rp-bis" type="date" value={bis} onChange={(e) => { setzeBis(e.target.value) }} />
        </Feld>
      </Leiste>
      <p className="knoepfe">
        <Knopf
          disabled={replay.isPending}
          onClick={() => {
            // Der Server nimmt die drei Werte in der Query entgegen, verlangt aber trotzdem einen
            // JSON-Körper (routes/admin.ts) — deshalb steht die Query im Pfad und `{}` im Körper.
            const geprueft = replayQuerySchema.safeParse({
              tenant,
              from: von === '' ? undefined : von,
              to: bis === '' ? undefined : bis,
            })
            if (!geprueft.success) {
              replay.reset()
              setzePruefFehler(ersterFehler(geprueft.error))
              return
            }
            setzePruefFehler(null)
            const parameter = new URLSearchParams({ tenant: geprueft.data.tenant })
            if (geprueft.data.from !== undefined) parameter.set('from', geprueft.data.from)
            if (geprueft.data.to !== undefined) parameter.set('to', geprueft.data.to)
            replay.mutate(`/api/admin/replay?${parameter.toString()}`)
          }}
        >
          Webhooks erneut einspielen
        </Knopf>
      </p>
      {replay.isSuccess && (
        <p className="meldung">
          {`Erneut eingespielt: ${replay.data.replayed} ${replay.data.replayed === 1 ? 'Ereignis' : 'Ereignisse'}.`}
        </p>
      )}
      {replay.isError && <p role="alert" className="fehler">{fehlermeldung(replay.error)}</p>}
    </section>
  )
}

// -----------------------------------------------------------------------------------------------------
// Testmail
// -----------------------------------------------------------------------------------------------------

function Testmail(): ReactElement {
  const [mail, setzeMail] = useState('')
  const [pruefFehler, setzePruefFehler] = useState<Eingabefehler | null>(null)

  const testmail = useMutation({
    mutationFn: (to: string) =>
      api.post<{ ok: true; outbox_id: string; to: string; template: string }>(
        '/api/admin/test-email', { to },
      ),
  })

  return (
    <section aria-label="Testmail">
      <h2>Testmail</h2>
      <p className="hinweis">
        Geht durch dieselbe Strecke wie jede echte Mail: Warteschlange, Versandlauf, Absenderdomain.
        Sie trägt keine Geschäftsdaten.
      </p>
      <Leiste beschriftung="Testmail versenden">
        <Feld label="Empfänger der Testmail" htmlFor="tm-to" fehler={meldungFuer(pruefFehler, 'to')}>
          <input id="tm-to" type="email" value={mail} onChange={(e) => { setzeMail(e.target.value) }} />
        </Feld>
      </Leiste>
      <p className="knoepfe">
        <Knopf
          disabled={testmail.isPending}
          onClick={() => {
            const geprueft = testEmailSchema.safeParse({ to: mail })
            if (!geprueft.success) {
              testmail.reset()
              setzePruefFehler(ersterFehler(geprueft.error))
              return
            }
            setzePruefFehler(null)
            testmail.mutate(geprueft.data.to)
          }}
        >
          Testmail senden
        </Knopf>
      </p>
      {testmail.isSuccess && (
        <p className="meldung">Testmail an {testmail.data.to} liegt in der Warteschlange.</p>
      )}
      {testmail.isError && <p role="alert" className="fehler">{fehlermeldung(testmail.error)}</p>}
    </section>
  )
}
